import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type TPSIncident = {
  occurrence_number: string;
  type: string;
  datetime: string;
  intersection: string;
  address?: string;
  neighbourhood?: string;
  division?: string;
  status?: string;
  notes?: string;
  vehicle?: {
    make?: string;
    model?: string;
    year?: string;
    color?: string;
    plate?: string;
  };
};

/** RAG source citation returned with AI responses */
export type RagSourceCitation = {
  /** Unique identifier for the source/chunk */
  id: string;
  /** Display title of the source document */
  title: string;
  /** Explicit document title when distinct from filename */
  documentTitle?: string;
  /** Original filename for secondary display */
  fileName?: string;
  filename?: string;
  /** Repository name where the source is stored */
  repository: string;
  /** Repository ID for navigation */
  repositoryId: string;
  /** Relevance score (0-1) */
  score: number;
  /** Preview text snippet from the source */
  preview?: string;
  /** Document ID for linking to full document */
  documentId: string;
  /** Optional page or section number */
  pageNumber?: number | string;
  /** Optional chunk index within the document */
  chunkIndex?: number;
  /** Section path within the document */
  sectionPath?: string;
};

export type OfficerChatMessage = {
  id: string;
  message: string;
  timestamp: number;
  from: {
    identity: string;
    name?: string;
    isAgent?: boolean;
  };
  tpsResults?: {
    results: TPSIncident[];
    totalFound: number;
  };
  /** RAG sources used to generate the AI response */
  sources?: RagSourceCitation[];
};

export interface OfficerChatState {
  messages: OfficerChatMessage[];
}

const initialState: OfficerChatState = {
  messages: [],
};

const officerChatSlice = createSlice({
  name: 'officerChat',
  initialState,
  reducers: {
    addOfficerChatMessage: (state, action: PayloadAction<OfficerChatMessage>) => {
      // Prevent duplicates based on ID
      const existingById = state.messages.findIndex(
        (m) => m.id === action.payload.id
      );
      if (existingById !== -1) {
        return; // Already exists by ID
      }

      // Prevent duplicates based on content + sender (within recent history)
      // This handles cases where multiple handlers fire and generate different IDs.
      // Ensure message is a string before calling trim()
      const messageValue = action.payload.message;
      const normalizedIncoming = (typeof messageValue === 'string' ? messageValue : String(messageValue || '')).trim();
      if (normalizedIncoming.length > 0) {
        const recentWindow = state.messages.slice(-50); // only check the most recent messages
        const existsSameContent = recentWindow.some(
          (m) => {
            const existingMsg = m.message;
            const normalizedExisting = (typeof existingMsg === 'string' ? existingMsg : String(existingMsg || '')).trim();
            return normalizedExisting === normalizedIncoming && m.from.identity === action.payload.from.identity;
          }
        );
        if (existsSameContent) {
          return; // Duplicate content from same sender already recorded
        }
      }

      state.messages.push(action.payload);
    },

    setOfficerChatMessages: (state, action: PayloadAction<OfficerChatMessage[]>) => {
      state.messages = action.payload;
    },

    /**
     * Remove a chat message by id. Used to scrub upstream error strings
     * (e.g. "Error processing your question: AI Engine error: 503") out of
     * the visible thread before they pollute conversation history.
     */
    removeOfficerChatMessage: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter((m) => m.id !== action.payload);
    },

    clearOfficerChatMessages: (state) => {
      state.messages = [];
    },
  },
});

export const {
  addOfficerChatMessage,
  setOfficerChatMessages,
  removeOfficerChatMessage,
  clearOfficerChatMessages,
} = officerChatSlice.actions;

export default officerChatSlice.reducer;
